import { useEffect, useState, type ComponentType, type ReactNode } from 'react'
import {
  Bot,
  Camera,
  Check,
  ListTodo,
  Mail,
  MessageCircle,
  Nfc,
  Phone,
  Plug,
  RefreshCw,
  Users,
  ShieldCheck,
  Tag,
  Wallet,
  type LucideProps,
  Monitor,
  KeyRound,
} from 'lucide-react'
import { STANDARD_STICHWOERTER, type AppConfig } from '@k4ia/shared'
import {
  getConfig,
  getIntroLinks,
  getMailLog,
  getTestSync,
  testSyncAbschluss,
  testSyncDateien,
  testSyncInhalte,
  listTeamsChannels,
  sendTestCall,
  sendTestEsl,
  sendTestSmenso,
  sendTestMail,
  updateConfig,
  getTestSyncAenderungen,
  TEST_SYNC_FERTIG_EVENT,
  type SyncAenderungen,
  type AdminTeamsChannel,
  type MailLogEntry,
  type TestCallResult,
  type TestEslResult,
  type TestSmensoResult,
  type TestMailResult,
} from '../lib/api'
import { PasskeysSection } from '../components/PasskeysSection'

export function ConfigPage() {
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [speicherFehler, setSpeicherFehler] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [introLinks, setIntroLinks] = useState<Awaited<ReturnType<typeof getIntroLinks>> | null>(
    null,
  )
  const [teams, setTeams] = useState<{
    channels: AdminTeamsChannel[]
    fromEnv: boolean
    botConfigured: boolean
  } | null>(null)

  useEffect(() => {
    void getConfig().then((r) => setConfig(r.config))
    void getIntroLinks()
      .then(setIntroLinks)
      .catch(() => undefined)
    void listTeamsChannels()
      .then(setTeams)
      .catch(() => undefined)
  }, [])

  if (!config) return <p className="text-text-muted">Lädt …</p>

  async function handleSave() {
    if (!config) return
    setStatus('saving')
    setSpeicherFehler(null)
    try {
      const { config: updated } = await updateConfig(
        config as unknown as Record<string, string | number | boolean | null>,
      )
      setConfig(updated)
      setDirty(false)
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 1500)
    } catch (err) {
      // Vorher blieb der Knopf bei einem Fehler für immer auf „Speichert …“.
      setSpeicherFehler(err instanceof Error ? err.message : String(err))
      setStatus('idle')
    }
  }

  function set<K extends keyof AppConfig>(key: K, value: AppConfig[K]) {
    setConfig((c) => (c ? { ...c, [key]: value } : c))
    setDirty(true)
  }

  return (
    <div className="max-w-3xl space-y-10 md:pb-24">
      <header className="space-y-2">
        <h2 className="page-title">Konfiguration</h2>
        <p className="text-sm text-text-muted">
          Diese Werte wirken sofort auf alle Kanäle, ohne Deployment — siehe docs/OPERATIONS.md für
          Empfehlungen.
        </p>
      </header>

      <PasskeysSection Section={Section} icon={KeyRound} />

      <Section
        title="Dienste"
        icon={Plug}
        tile="#63c3d1"
        glyph="#1d1d1f"
        footer={
          <>
            Der entscheidende Schalter je Fähigkeit: <code className="font-mono">mock</code>{' '}
            antwortet mit einer festen Attrappe und kostet nichts, alles andere ruft den echten
            Dienst auf. Ein hinterlegter Schlüssel allein schaltet nichts scharf — die Umstellung
            ist eine bewusste Entscheidung und wirkt sofort, ohne Deployment.
          </>
        }
      >
        <Row label="Sprachmodell" badge={<LiveBadge mock={config.llmProvider === 'mock'} />}>
          <select
            className="input"
            value={config.llmProvider}
            onChange={(e) => set('llmProvider', e.target.value as AppConfig['llmProvider'])}
          >
            <option value="mock">mock (feste Antwort, kostenlos)</option>
            <option value="anthropic">anthropic (echte Antworten)</option>
          </select>
        </Row>
        <Row
          label="Einbettungen"
          hint="Wissenssuche"
          badge={<LiveBadge mock={config.embeddingProvider === 'mock'} />}
        >
          <select
            className="input"
            value={config.embeddingProvider}
            onChange={(e) =>
              set('embeddingProvider', e.target.value as AppConfig['embeddingProvider'])
            }
          >
            <option value="mock">mock (findet nichts Echtes)</option>
            <option value="workers-ai">workers-ai</option>
          </select>
        </Row>
        <Row label="Bilderkennung" badge={<LiveBadge mock={config.visionProvider === 'mock'} />}>
          <select
            className="input"
            value={config.visionProvider}
            onChange={(e) => set('visionProvider', e.target.value as AppConfig['visionProvider'])}
          >
            <option value="mock">mock (fester Treffer)</option>
            <option value="workers-ai">workers-ai</option>
          </select>
        </Row>
        <Row label="Sprachausgabe" badge={<LiveBadge mock={config.voiceProvider === 'mock'} />}>
          <select
            className="input"
            value={config.voiceProvider}
            onChange={(e) => set('voiceProvider', e.target.value as AppConfig['voiceProvider'])}
          >
            <option value="mock">mock (stumm)</option>
            <option value="elevenlabs-agents">elevenlabs-agents</option>
            <option value="elevenlabs-pipeline">elevenlabs-pipeline</option>
          </select>
        </Row>
        <Row label="Spracherkennung" badge={<LiveBadge mock={config.sttProvider === 'mock'} />}>
          <select
            className="input"
            value={config.sttProvider}
            onChange={(e) => set('sttProvider', e.target.value as AppConfig['sttProvider'])}
          >
            <option value="mock">mock</option>
            <option value="elevenlabs">elevenlabs</option>
          </select>
        </Row>
      </Section>

      <Section title="Modelle & Stimme" icon={Bot} tile="#d2436d">
        <Row label="LLM-Modell schnell" hint="Dialog und Sprache">
          <input
            className="input font-mono"
            value={config.llmModelFast}
            onChange={(e) => set('llmModelFast', e.target.value)}
          />
        </Row>
        <Row label="LLM-Modell stark" hint="Komplexe Fragen und Vision">
          <input
            className="input font-mono"
            value={config.llmModelStrong}
            onChange={(e) => set('llmModelStrong', e.target.value)}
          />
        </Row>
        <Row label="LLM-Temperatur" hint="Leer = nicht mitschicken">
          <input
            type="number"
            step="0.1"
            min="0"
            max="1"
            className="input text-right tabular-nums"
            placeholder="—"
            // Leer heißt "nicht mitschicken": die aktuellen Claude-Modelle lehnen
            // temperature ab und brechen die Anfrage sonst mit 400 ab.
            value={config.llmTemperature ?? ''}
            onChange={(e) =>
              set('llmTemperature', e.target.value === '' ? null : Number(e.target.value))
            }
          />
        </Row>
        <Row label="Nachdenken" hint="Vor der Antwort; kostet Zeit bis zum ersten Wort">
          <select
            className="input"
            value={config.llmThinking}
            onChange={(e) => set('llmThinking', e.target.value as AppConfig['llmThinking'])}
          >
            <option value="off">aus (schnellstes erstes Wort)</option>
            <option value="adaptive">adaptiv (das Modell entscheidet)</option>
          </select>
        </Row>
        <Row label="ElevenLabs Voice-ID">
          <input
            className="input font-mono"
            value={config.voiceId}
            onChange={(e) => set('voiceId', e.target.value)}
          />
        </Row>
        <div className="flex flex-col gap-2 px-5 py-4">
          <label htmlFor="dazwischen-woerter" className="text-[0.95rem] font-medium text-text">
            Stichwörter zum Unterbrechen
          </label>
          <span className="text-sm text-text-muted">
            Sonst muss man im Sprachmodus eine Sekunde sprechen, bis K4ia aufhört. Diese Wörter
            unterbrechen sie sofort. Eins je Zeile oder durch Komma getrennt; leer heißt: die
            Vorgabe (Stopp, Halt, Reicht, Warte, Nächster, Weiter, Nochmal …). „Ja“, „Okay“ und
            „Mhm“ besser nicht aufnehmen, damit bestätigt man beim Zuhören.
          </span>
          <textarea
            id="dazwischen-woerter"
            rows={3}
            className="input w-full font-mono"
            placeholder={STANDARD_STICHWOERTER.slice(0, 12).join(', ') + ' …'}
            value={config.dazwischenWoerter}
            onChange={(e) => set('dazwischenWoerter', e.target.value)}
          />
        </div>
      </Section>

      <Section title="Gespräch" icon={MessageCircle} tile="#f08046">
        <Row label="Standard-Anrede">
          <select
            className="input"
            value={config.defaultAddressForm}
            onChange={(e) => set('defaultAddressForm', e.target.value as 'du' | 'sie')}
          >
            <option value="du">Duzen</option>
            <option value="sie">Siezen</option>
          </select>
        </Row>
        <Row
          label="„Vorlesen“ nach dem Tag-Scan"
          hint="Aus: Nach dem Scan eines Showcase-Tags gibt es nur „Im Chat lesen“"
          inline
        >
          <Switch checked={config.tagVorlesen} onChange={(v) => set('tagVorlesen', v)} />
        </Row>
        <Row label="Websuche erlauben" inline>
          <Switch checked={config.webSearchEnabled} onChange={(v) => set('webSearchEnabled', v)} />
        </Row>
        <Row
          label="Wissenssuche vorab"
          hint="Vor der ersten Modellrunde — schneller im Sprachmodus"
          inline
        >
          <Switch checked={config.ragPrefetch} onChange={(v) => set('ragPrefetch', v)} />
        </Row>
        <Row
          label="Gespräch läuft ab nach"
          hint="Stunden ohne Nachricht — danach beginnt beim nächsten Öffnen ein neues Gespräch am Handy; 0 = nie. Das alte bleibt in der Datenbank."
        >
          <Unit unit="Std.">
            <input
              type="number"
              step="1"
              min="0"
              max="168"
              className="input text-right tabular-nums"
              value={config.chatTimeoutHours}
              onChange={(e) => set('chatTimeoutHours', Number(e.target.value))}
            />
          </Unit>
        </Row>
        <Row
          label="Rückfrage beim Wiederkommen ab"
          hint="Minuten Pause seit der letzten Nachricht, ab denen der Chat „weitermachen oder neu anfangen?“ fragt; 0 = bei jedem Öffnen"
        >
          <Unit unit="Min.">
            <input
              type="number"
              step="1"
              min="0"
              max="1440"
              className="input text-right tabular-nums"
              value={config.chatResumeHintMinutes}
              onChange={(e) => set('chatResumeHintMinutes', Number(e.target.value))}
            />
          </Unit>
        </Row>
        <Row
          label="Mindestähnlichkeit"
          hint="Treffer der Vorab-Suche darunter werden nicht gezeigt (0–1)"
        >
          <input
            type="number"
            step="0.05"
            min="0"
            max="1"
            className="input"
            value={config.ragMinScore}
            onChange={(e) => set('ragMinScore', Number(e.target.value))}
          />
        </Row>
        <Row label="Überbrückungswort" hint="Im Sprachmodus, leer = aus">
          <input
            className="input"
            placeholder="aus"
            value={config.voiceFillerText}
            onChange={(e) => set('voiceFillerText', e.target.value)}
          />
        </Row>
        <Row label="Überbrückungswort nach" hint="Millisekunden ohne Inhalt">
          <Unit unit="ms">
            <input
              type="number"
              step="100"
              min="200"
              max="10000"
              className="input text-right tabular-nums"
              value={config.voiceFillerAfterMs}
              onChange={(e) => set('voiceFillerAfterMs', Number(e.target.value))}
            />
          </Unit>
        </Row>
      </Section>

      <Section
        title="Intro"
        icon={Nfc}
        tile="#63c3d1"
        glyph="#1d1d1f"
        footer={
          <>
            Das Intro startet, wenn am Handy nach dem Scan „Intro starten" getippt wird. Danach
            zeigen Bildschirm und Handy eine Box je Showcase mit Status „live", in der Reihenfolge
            unter Showcases; wer eine antippt, hört K4ia den Langtext dazu erzählen. Den Bildschirm
            einmal anklicken, damit der Browser den Ton freigibt. Die Tonspuren erzeugt ElevenLabs
            mit der Stimme des Agenten beim ersten Abspielen nach einer Textänderung, danach kommen
            sie aus dem Speicher. Die Tag-URL ändert sich nur, wenn sich{' '}
            <code className="font-mono">SESSION_SIGNING_SECRET</code> ändert.
          </>
        }
      >
        <div className="space-y-2 px-5 py-4">
          <label htmlFor="intro-text" className="block text-[0.95rem] font-medium text-text">
            Intro-Text
          </label>
          <span className="block text-xs text-text-muted">
            Spricht K4ia auf dem Intro-Bildschirm, sobald am Handy „Intro starten“ getippt wird
          </span>
          <textarea
            id="intro-text"
            rows={5}
            className="input w-full"
            value={config.introText}
            onChange={(e) => set('introText', e.target.value)}
          />
        </div>
        {introLinks && (
          <>
            <LinkRow label="Bildschirm" hint="Auf dem Laptop öffnen" url={introLinks.screenUrl} />
            <LinkRow label="Tag-URL" hint="Aufs NFC-Tag schreiben" url={introLinks.triggerUrl} />
            <LinkRow
              label="Bühne: Bildschirm"
              hint="Ein Bildschirm für Intro, Gespräch und Gewinnspiel — einmal hineinklicken"
              url={introLinks.buehne.screenUrl}
            />
            <LinkRow
              label="Bühne: Handy"
              hint="Gemeinsame Fernbedienung: oben umschalten, was der Bildschirm zeigt — nur fürs Team"
              url={introLinks.buehne.remoteUrl}
            />
          </>
        )}
      </Section>

      <Section
        title="Schaufenster"
        icon={Monitor}
        tile="#63c3d1"
        glyph="#1d1d1f"
        footer={
          <>
            Wie das Intro, nur nach draußen: Der Bildschirm zeigt einen QR-Code, der alle drei
            Minuten wechselt. Wer ihn scannt, steuert das Fenster vom Handy — immer nur eine Person;
            nach zwei Minuten ohne Bedienung ist es wieder frei. Im Fenster stehen nur K4ias
            Antworten, nie die Fragen. Der Bildschirm startet ohne Klick; damit er Ton hat, Chrome
            mit <code className="font-mono">--autoplay-policy=no-user-gesture-required</code>{' '}
            starten. Die Adresse enthält den Schlüssel des Bildschirms — nicht weitergeben.
          </>
        }
      >
        <div className="space-y-2 px-5 py-4">
          <label htmlFor="schaufenster-text" className="block text-[0.95rem] font-medium text-text">
            Begrüßung
          </label>
          <span className="block text-xs text-text-muted">
            Spricht K4ia im Schaufenster, sobald jemand den QR-Code scannt
          </span>
          <textarea
            id="schaufenster-text"
            rows={4}
            className="input w-full"
            value={config.schaufensterText}
            onChange={(e) => set('schaufensterText', e.target.value)}
          />
        </div>
        {introLinks && (
          <LinkRow
            label="Bildschirm"
            hint="Im Schaufenster öffnen (mit Schlüssel)"
            url={introLinks.schaufensterUrl}
          />
        )}
      </Section>

      <Section title="Kamera" icon={Camera} tile="#17727e">
        <Row label="Erkennungsschwelle" hint="Showcase gilt ab dieser Sicherheit als erkannt">
          <input
            type="number"
            step="0.05"
            min="0"
            max="1"
            className="input text-right tabular-nums"
            value={config.showcaseRecognitionThreshold}
            onChange={(e) => set('showcaseRecognitionThreshold', Number(e.target.value))}
          />
        </Row>
        <Row label="Ansage im Sprachmodus" hint="Wenn die Kamera einen Showcase erkennt">
          <select
            className="input"
            value={config.cameraVoiceAnnounce}
            onChange={(e) =>
              set('cameraVoiceAnnounce', e.target.value as AppConfig['cameraVoiceAnnounce'])
            }
          >
            <option value="explain">K4ia sagt, wo der Besucher steht, und erklärt kurz</option>
            <option value="offer">K4ia sagt, wo er steht, und bietet an</option>
            <option value="off">K4ia schweigt (nur Hinweis auf dem Bildschirm)</option>
          </select>
        </Row>
        <Row label="Stabile Bilder bis zur Erkennung">
          <input
            type="number"
            step="1"
            min="1"
            className="input text-right tabular-nums"
            value={config.showcaseRecognitionStableFrames}
            onChange={(e) => set('showcaseRecognitionStableFrames', Number(e.target.value))}
          />
        </Row>
      </Section>

      <Section
        title="Microsoft Teams"
        icon={Users}
        tile="#4b53bc"
        footer="Der Meldekanal ist ein Kanal, in den K4ia eingeladen wurde — er erscheint hier, sobald jemand dort „@K4ia“ geschrieben hat oder K4ia dem Team hinzugefügt wurde. Dorthin gehen „Mitarbeiter rufen“ und neue Wissenslücken. Ansprechpartner mit Mailadresse (Showcase → Ansprechpartner) bekommen bei einer Rückfrage eine Direktnachricht."
      >
        <Row
          label="Meldekanal"
          badge={<LiveBadge mock={!teams?.botConfigured} />}
          hint={
            teams && teams.channels.length === 0
              ? 'Noch kein Kanal bekannt — K4ia zuerst in ein Team einladen.'
              : undefined
          }
        >
          <select
            className="input"
            value={config.teamsStaffConversationId}
            onChange={(e) => {
              const kanal = teams?.channels.find((k) => k.conversationId === e.target.value)
              set('teamsStaffConversationId', e.target.value)
              set('teamsStaffServiceUrl', kanal?.serviceUrl ?? '')
            }}
          >
            <option value="">
              {teams?.fromEnv ? 'Aus der Umgebung (Secrets)' : 'Keiner (Meldungen nur im Log)'}
            </option>
            {(teams?.channels ?? []).map((k) => (
              <option key={k.conversationId} value={k.conversationId}>
                {[k.teamName, k.channelName].filter(Boolean).join(' › ') ||
                  `${k.conversationType} ${k.conversationId.slice(0, 18)}…`}
              </option>
            ))}
          </select>
        </Row>
        <Row label="Wissenslücken melden" hint="Neue offene Fragen in den Meldekanal" inline>
          <Switch checked={config.teamsGapNotify} onChange={(v) => set('teamsGapNotify', v)} />
        </Row>
        <Row
          label="Ansprechpartner anschreiben"
          hint="Direktnachricht bei Rückfragen zu ihrem Showcase"
          inline
        >
          <Switch
            checked={config.teamsContactNotify}
            onChange={(v) => set('teamsContactNotify', v)}
          />
        </Row>
      </Section>

      <Section
        title="E-Mail"
        icon={Mail}
        tile="#0078d4"
        footer="K4ia verschickt Mails über Microsoft 365 aus dem Postfach in MS_GRAPH_USER_ID: die Besuchs-Zusammenfassung und die Eingangsbestätigung nach einer Rückfrage. Kommt nichts an, zeigt die Testmail den genauen Grund von Microsoft."
      >
        <Row
          label="Eingangsbestätigung"
          hint="Besucher mit Mailadresse bekommen nach einer Rückfrage eine Bestätigung"
          inline
        >
          <Switch
            checked={config.followupConfirmMail}
            onChange={(v) => set('followupConfirmMail', v)}
          />
        </Row>
        <TestMailRow />
        <MailLogRow />
      </Section>

      <Section
        title="Smenso"
        icon={ListTodo}
        tile="#5b5bd6"
        footer="Rückfragen von Besuchern landen als Aufgabe im Smenso-Projekt „K4ia“ — angelegt von dem Benutzer, dem der Schlüssel in SMENSO_API_TOKEN gehört (docs/SETUP.md §8b)."
      >
        <TestSmensoRow />
      </Section>

      <Section
        title="Preisschilder (ESL)"
        icon={Tag}
        tile="#0f766e"
        footer="K4ia liest die Preise der elektronischen Preisschilder (Collection J4P) für jeden und ändert sie nur für das Team: im Bühnen-Chat, in Teams, am Telefon der Rufbereitschaft. Schlüssel ESL_API_KEY nur in Production; Test läuft gegen die Attrappe (docs/SETUP.md §8d)."
      >
        <TestEslRow />
        {introLinks && (
          <LinkRow
            label="Team-Link fürs Handy"
            hint="Öffnet den normalen Chat als Team-Sitzung — nur ans Team geben, Besucher bleiben Besucher"
            url={introLinks.teamUrl}
          />
        )}
      </Section>

      <Section
        title="Live-Wand"
        icon={Monitor}
        tile="#63c3d1"
        footer="Der Bildschirm im Lab unter www.k4ia.ai/wand: wie viele gerade mit K4ia sprechen, welche Stände frei sind, was heute beliebt war. Zählungen, keine Personen."
      >
        <Row
          label="Letzte Fragen zeigen"
          hint="Kurze Sachfragen des Tages auf der Wand. Mailadressen, Nummern und Namen bleiben immer draußen."
          inline
        >
          <Switch checked={config.wandFragenZeigen} onChange={(v) => set('wandFragenZeigen', v)} />
        </Row>
      </Section>

      <Section
        title="Telefon"
        icon={Phone}
        tile="#f22f46"
        footer="Über ElevenLabs und eine Twilio-Nummer. Einrichtung Schritt für Schritt: docs/TELEFON-EINRICHTUNG.md. Jede Gesprächsminute kostet (ElevenLabs, Twilio, KI-Modell)."
      >
        <Row
          label="Rufbereitschaft anrufen"
          hint="Will im Lab jemand mit einem Menschen sprechen, ruft K4ia zusätzlich zur Teams-Meldung an"
          inline
        >
          <Switch checked={config.telefonAnrufen} onChange={(v) => set('telefonAnrufen', v)} />
        </Row>
        <div className="flex flex-col gap-2 px-5 py-4">
          <label htmlFor="telefon-rufbereitschaft" className="text-[0.95rem] font-medium text-text">
            Rufbereitschaft
          </label>
          <span className="text-sm text-text-muted">
            Eine Person je Zeile, „Name: +49 170 1234567“. Die erste zuerst; lässt sich der Anruf
            nicht aufbauen, die nächste. Ob jemand abnimmt, sieht K4ia nicht — dafür bleibt die
            Teams-Meldung.
          </span>
          <textarea
            id="telefon-rufbereitschaft"
            rows={3}
            className="input w-full font-mono"
            placeholder={'Flo: +49 170 1234567'}
            value={config.telefonRufbereitschaft}
            onChange={(e) => set('telefonRufbereitschaft', e.target.value)}
          />
        </div>
        <Row label="Anrufzeiten" hint="Zeit in Berlin, z. B. „Mo-Fr 09:00-18:00“ — leer: immer">
          <input
            className="input"
            value={config.telefonZeiten}
            onChange={(e) => set('telefonZeiten', e.target.value)}
          />
        </Row>
        <Row label="Anrufe annehmen" hint="Unter der Nummer mit K4ia telefonieren" inline>
          <Switch checked={config.telefonEingehend} onChange={(v) => set('telefonEingehend', v)} />
        </Row>
        <Row
          label="Nur bekannte Nummern"
          hint="Nur die Rufbereitschaft darf anrufen — schützt vor Kosten durch Fremde"
          inline
        >
          <Switch
            checked={config.telefonNurBekannte}
            onChange={(v) => set('telefonNurBekannte', v)}
          />
        </Row>
        <div className="flex flex-col gap-2 px-5 py-4">
          <label htmlFor="telefon-begruessung" className="text-[0.95rem] font-medium text-text">
            Begrüßung am Telefon
          </label>
          <span className="text-sm text-text-muted">
            {'{name}'} wird zu „ Flo“, wenn die Nummer in der Rufbereitschaft steht
          </span>
          <input
            id="telefon-begruessung"
            className="input w-full"
            value={config.telefonBegruessung}
            onChange={(e) => set('telefonBegruessung', e.target.value)}
          />
        </div>
        <TestCallRow />
      </Section>

      <Section
        title="Kosten"
        icon={Wallet}
        tile="#047857"
        footer="Die drei Preise sind die Grundlage der Kostengrenzen. Sie sind Vorgabewerte und gehören vor dem produktiven Betrieb gegen die aktuelle Preisliste des Anbieters und das gewählte Modell gesetzt — eine Grenze mit veralteten Preisen schützt vor der falschen Zahl."
      >
        <Row label="Limit pro Sitzung">
          <Unit unit="€">
            <input
              type="number"
              step="0.5"
              className="input text-right tabular-nums"
              value={config.costLimitPerSessionEur}
              onChange={(e) => set('costLimitPerSessionEur', Number(e.target.value))}
            />
          </Unit>
        </Row>
        <Row label="Limit pro Tag">
          <Unit unit="€">
            <input
              type="number"
              step="10"
              className="input text-right tabular-nums"
              value={config.costLimitPerDayEur}
              onChange={(e) => set('costLimitPerDayEur', Number(e.target.value))}
            />
          </Unit>
        </Row>
        <Row label="Preis Eingabe" hint="Je 1 Mio. Token">
          <Unit unit="€">
            <input
              type="number"
              step="0.1"
              min="0"
              className="input text-right tabular-nums"
              value={config.llmPriceInputPerMTokenEur}
              onChange={(e) => set('llmPriceInputPerMTokenEur', Number(e.target.value))}
            />
          </Unit>
        </Row>
        <Row label="Preis Ausgabe" hint="Je 1 Mio. Token">
          <Unit unit="€">
            <input
              type="number"
              step="0.1"
              min="0"
              className="input text-right tabular-nums"
              value={config.llmPriceOutputPerMTokenEur}
              onChange={(e) => set('llmPriceOutputPerMTokenEur', Number(e.target.value))}
            />
          </Unit>
        </Row>
        <Row label="Preis zwischengespeicherte Eingabe" hint="Je 1 Mio. Token">
          <Unit unit="€">
            <input
              type="number"
              step="0.05"
              min="0"
              className="input text-right tabular-nums"
              value={config.llmPriceCachedInputPerMTokenEur}
              onChange={(e) => set('llmPriceCachedInputPerMTokenEur', Number(e.target.value))}
            />
          </Unit>
        </Row>
      </Section>

      <Section
        title="Testumgebung"
        icon={RefreshCw}
        tile="#636366"
        footer="Production ist das führende System: Inhalte hier pflegen und bei Bedarf nach Test kopieren. Kopiert werden Showcases mit Medien, Wissen und Dokumente, NFC-Tags, Rundgang, Erkennungshinweise, Prompts und Einstellungen. Nicht kopiert: Gespräche, Leads, Statistik, Protokoll, Teams-Kanal und Telefon-Einstellungen von Test."
      >
        <TestSyncRow />
      </Section>

      <Section title="Datenschutz" icon={ShieldCheck} tile="#636366">
        <Row label="Löschfrist Gesprächsverläufe">
          <Unit unit="Tage">
            <input
              type="number"
              className="input text-right tabular-nums"
              value={config.conversationRetentionDays}
              onChange={(e) => set('conversationRetentionDays', Number(e.target.value))}
            />
          </Unit>
        </Row>
      </Section>

      {/* Schwebt über dem Inhalt, damit Speichern nach dem Scrollen nicht gesucht werden muss. */}
      <div className="sticky-actions md:bottom-6">
        <div className="glass flex items-center justify-between gap-4 rounded-full border border-border py-2 pl-5 pr-2 shadow-[var(--shadow-card)] sm:pl-6">
          <span
            className={`text-sm ${speicherFehler ? 'text-status-error' : 'text-text-muted'}`}
            aria-live="polite"
          >
            {speicherFehler
              ? 'Nicht gespeichert — siehe Meldung oben'
              : status === 'saved'
                ? 'Gespeichert — wirkt ab sofort'
                : dirty
                  ? 'Ungespeicherte Änderungen'
                  : 'Alles gespeichert'}
          </span>
          <button
            onClick={() => void handleSave()}
            className="btn-primary flex items-center gap-2"
            disabled={status === 'saving'}
          >
            {status === 'saved' && <Check size={16} strokeWidth={2.5} aria-hidden="true" />}
            {status === 'saving' ? 'Speichert …' : status === 'saved' ? 'Gespeichert' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Eine Gruppe wie in den iOS-Einstellungen: Überschrift, Karte mit Zeilen, Fußnote. */
function Section({
  title,
  icon: Icon,
  tile,
  glyph = '#ffffff',
  footer,
  children,
}: {
  title: string
  icon: ComponentType<LucideProps>
  tile: string
  glyph?: string
  footer?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3 px-1">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-inset ring-border"
          style={{ background: tile, color: glyph }}
        >
          <Icon size={18} strokeWidth={2.25} />
        </span>
        <h3 className="section-title">{title}</h3>
      </div>
      <div className="card divide-y divide-border overflow-hidden">{children}</div>
      {footer && <p className="px-5 text-xs leading-relaxed text-text-muted">{footer}</p>}
    </section>
  )
}

/**
 * Beschriftung links, Eingabe rechts; auf schmalen Bildschirmen untereinander.
 * `inline` hält kleine Bedienelemente (Schalter) auch dort in einer Zeile.
 */
function Row({
  label,
  hint,
  badge,
  inline = false,
  children,
}: {
  label: string
  hint?: string
  badge?: ReactNode
  inline?: boolean
  children: ReactNode
}) {
  return (
    <label
      className={`flex gap-3 px-5 py-4 ${
        inline ? 'items-center justify-between' : 'flex-col sm:flex-row sm:items-center'
      }`}
    >
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-[0.95rem] font-medium text-text">
          {label}
          {badge}
        </span>
        {hint && <span className="mt-0.5 block text-xs text-text-muted">{hint}</span>}
      </span>
      <span className={inline ? 'shrink-0' : 'w-full sm:w-64 sm:shrink-0'}>{children}</span>
    </label>
  )
}

/** Testmail an eine Adresse — zeigt Erfolg oder den Grund von Microsoft. */
function TestMailRow() {
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<TestMailResult | null>(null)

  async function senden() {
    setBusy(true)
    setResult(null)
    try {
      setResult(await sendTestMail(to))
    } catch (err) {
      setResult({ ok: false, error: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <span className="text-[0.95rem] font-medium text-text">Testmail senden</span>
      <span className="flex gap-2">
        <input
          type="email"
          className="input min-w-0 flex-1"
          placeholder="name@j4p.de"
          aria-label="Empfänger der Testmail"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <button
          type="button"
          className="btn-primary shrink-0"
          disabled={busy || !to.includes('@')}
          onClick={() => void senden()}
        >
          {busy ? 'Sendet …' : 'Senden'}
        </button>
      </span>
      {result && (
        <div aria-live="polite" className="space-y-1 text-sm">
          {result.ok ? (
            <p className="text-text">
              Verschickt{result.from ? ` von ${result.from}` : ''}. Kommt sie nicht an, liegt sie
              vielleicht im Spam.
            </p>
          ) : (
            <>
              {result.hint && <p className="font-medium text-text">{result.hint}</p>}
              {result.error && (
                <p className="break-all font-mono text-xs text-text-muted">{result.error}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

const MAIL_ART: Record<string, string> = {
  zusammenfassung: 'Besuchs-Zusammenfassung',
  bestaetigung: 'Eingangsbestätigung',
}

/** Was aus dem Chat heraus verschickt wurde — und warum nicht, wenn nicht. */
function MailLogRow() {
  const [entries, setEntries] = useState<MailLogEntry[] | null>(null)

  async function laden() {
    try {
      setEntries((await getMailLog()).entries)
    } catch {
      setEntries([])
    }
  }
  useEffect(() => {
    void laden()
  }, [])

  return (
    <div className="flex flex-col gap-2 px-5 py-4">
      <span className="flex items-center justify-between gap-2">
        <span className="text-[0.95rem] font-medium text-text">Zuletzt aus dem Chat</span>
        <button
          type="button"
          className="text-sm text-text-muted underline"
          onClick={() => void laden()}
        >
          Neu laden
        </button>
      </span>
      {entries === null ? null : entries.length === 0 ? (
        <p className="text-sm text-text-muted">Noch keine Mail aus dem Chat.</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {entries.map((e) => (
            <li key={e.at + e.art} className="space-y-0.5">
              <p className="text-text">
                {new Date(e.at).toLocaleString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                · {MAIL_ART[e.art] ?? e.art} ·{' '}
                <span className={e.ok ? 'font-medium' : 'font-medium text-status-error'}>
                  {e.ok ? 'verschickt' : 'nicht verschickt'}
                </span>
              </p>
              {e.hint && <p className="font-medium text-text">{e.hint}</p>}
              {e.error && <p className="break-all font-mono text-xs text-text-muted">{e.error}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const TABELLEN_NAME: Record<string, string> = {
  showcases: 'Showcases',
  assets: 'Medien',
  documents: 'Dokumente',
  nfc_tags: 'NFC-Tags',
  tour_stops: 'Rundgang-Stopps',
}

/** Production → Test, in drei Schritten (Inhalte, Dateien, Suchindex), mit Fortschritt. */
function TestSyncRow() {
  const [status, setStatus] = useState<{ moeglich: boolean; grund: string | null } | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [ergebnis, setErgebnis] = useState<{ ok: boolean; text: string } | null>(null)
  const [aenderungen, setAenderungen] = useState<SyncAenderungen | null>(null)
  const [alleZeigen, setAlleZeigen] = useState(false)

  const ladeAenderungen = () =>
    getTestSyncAenderungen()
      .then(setAenderungen)
      .catch(() => setAenderungen(null))

  useEffect(() => {
    getTestSync()
      .then(setStatus)
      .catch(() => setStatus({ moeglich: false, grund: 'Status nicht abrufbar' }))
    void ladeAenderungen()
  }, [])

  async function synchronisieren() {
    if (
      !window.confirm(
        'Alle Inhalte auf Test werden durch die aus Production ersetzt. Was nur auf Test steht, ist danach weg. Fortfahren?',
      )
    ) {
      return
    }
    setErgebnis(null)
    try {
      setLaeuft('Inhalte werden übertragen …')
      const { anzahl, fehlend } = await testSyncInhalte()
      let kopiert = 0
      const probleme: string[] = []
      for (let i = 0; i < fehlend.length; i += 5) {
        setLaeuft(`Dateien ${Math.min(i + 5, fehlend.length)} von ${fehlend.length} …`)
        const r = await testSyncDateien(fehlend.slice(i, i + 5))
        kopiert += r.kopiert
        probleme.push(...r.fehltHier.map((k) => `fehlt in Production: ${k}`))
        probleme.push(...r.fehler.map((f) => `${f.key}: ${f.error}`))
      }
      setLaeuft('Suchindex auf Test wird neu aufgebaut …')
      const { dokumente } = await testSyncAbschluss()
      // Liste und Hinweis oben: ab jetzt ist alles drüben.
      void ladeAenderungen()
      window.dispatchEvent(new Event(TEST_SYNC_FERTIG_EVENT))
      const zeilen = Object.entries(TABELLEN_NAME)
        .filter(([t]) => anzahl[t] !== undefined)
        .map(([t, name]) => `${anzahl[t]} ${name}`)
        .join(', ')
      setErgebnis({
        ok: probleme.length === 0,
        text: `Fertig: ${zeilen}. ${kopiert} neue Dateien kopiert, ${dokumente} Dokumente werden auf Test neu indexiert (dauert ein paar Minuten).${
          probleme.length ? ` Probleme: ${probleme.slice(0, 5).join('; ')}` : ''
        }`,
      })
    } catch (err) {
      setErgebnis({ ok: false, text: err instanceof Error ? err.message : String(err) })
    } finally {
      setLaeuft(null)
    }
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <span className="flex flex-wrap items-center justify-between gap-3">
        <span>
          <span className="block text-[0.95rem] font-medium text-text">
            Nach Test synchronisieren
          </span>
          <span className="block text-sm text-text-muted">
            Ersetzt die Inhalte auf test.k4ia.de durch die von hier. Läuft auch nachts von selbst,
            wenn etwas offen ist.
          </span>
        </span>
        <button
          type="button"
          className="btn-primary shrink-0"
          disabled={!status?.moeglich || laeuft !== null}
          onClick={() => void synchronisieren()}
        >
          {laeuft ? 'Läuft …' : 'Nach Test synchronisieren'}
        </button>
      </span>
      {status && !status.moeglich && status.grund && (
        <p className="text-sm text-text-muted">{status.grund}</p>
      )}
      {laeuft && (
        <p aria-live="polite" className="text-sm text-text">
          {laeuft}
        </p>
      )}
      {ergebnis && (
        <p
          aria-live="polite"
          className={`text-sm ${ergebnis.ok ? 'text-text' : 'font-medium text-status-error'}`}
        >
          {ergebnis.text}
        </p>
      )}
      {aenderungen && status?.moeglich && (
        <div
          className="rounded-2xl bg-surface-2/60 px-4 py-3 text-sm"
          data-testid="sync-aenderungen"
        >
          <p className="text-text-muted">
            Letzter Abgleich:{' '}
            {aenderungen.letzterAbgleich
              ? `${zeitpunkt(aenderungen.letzterAbgleich)}${aenderungen.letzterAbgleichVon ? ` (${aenderungen.letzterAbgleichVon})` : ''}`
              : 'noch nie'}
          </p>
          {aenderungen.letzterFehler && (
            <p role="alert" className="mt-1 font-medium text-status-error">
              Der automatische Abgleich am {zeitpunkt(aenderungen.letzterFehler.at)} ist
              fehlgeschlagen: {aenderungen.letzterFehler.text}
            </p>
          )}
          {aenderungen.anzahl === 0 ? (
            <p className="mt-1 text-text">Seitdem nichts geändert — Test ist auf dem Stand.</p>
          ) : (
            <>
              <p className="mt-1 font-medium text-text">
                Seitdem{' '}
                {aenderungen.anzahl === 1 ? 'eine Änderung' : `${aenderungen.anzahl} Änderungen`},
                noch nicht auf Test — spätestens heute Nacht automatisch:
              </p>
              <ul className="mt-2 space-y-1">
                {(alleZeigen ? aenderungen.eintraege : aenderungen.eintraege.slice(0, 10)).map(
                  (a, i) => (
                    <li key={`${a.at}-${i}`} className="flex flex-wrap gap-x-3 text-text">
                      <span className="shrink-0 font-mono text-xs text-text-muted">
                        {zeitpunkt(a.at)}
                      </span>
                      <span className="min-w-0 flex-1">{a.text}</span>
                      <span className="shrink-0 text-xs text-text-muted">{a.actor}</span>
                    </li>
                  ),
                )}
              </ul>
              {aenderungen.eintraege.length > 10 && (
                <button
                  type="button"
                  className="btn-chip mt-2"
                  onClick={() => setAlleZeigen((v) => !v)}
                >
                  {alleZeigen
                    ? 'Weniger anzeigen'
                    : `Alle ${aenderungen.eintraege.length} anzeigen`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** „01.10., 11:02“ — kurz, denn das Jahr ist immer dieses. */
function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function TestSmensoRow() {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<TestSmensoResult | null>(null)

  async function testen() {
    setBusy(true)
    setResult(null)
    try {
      setResult(await sendTestSmenso())
    } catch (err) {
      setResult({ ok: false, error: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <span className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-[0.95rem] font-medium text-text">Smenso testen</span>
          <span className="text-sm text-text-muted">
            Legt eine Testaufgabe „Verbindungstest aus dem K4ia-Admin“ an
          </span>
        </span>
        <button
          type="button"
          className="btn-primary shrink-0"
          disabled={busy}
          onClick={() => void testen()}
        >
          {busy ? 'Testet …' : 'Testen'}
        </button>
      </span>
      {result && (
        <div aria-live="polite" className="space-y-1 text-sm">
          {result.ok ? (
            <p className="text-text">
              Angelegt — die Aufgabe steht im Projekt „K4ia“ und kann gelöscht werden.
            </p>
          ) : (
            <>
              {result.hint && <p className="font-medium text-text">{result.hint}</p>}
              {result.error && (
                <p className="break-all font-mono text-xs text-text-muted">{result.error}</p>
              )}
            </>
          )}
          {result.keyId && (
            <p className="text-xs text-text-muted">Hinterlegter Schlüssel: Key-ID {result.keyId}</p>
          )}
        </div>
      )}
    </div>
  )
}

function TestEslRow() {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<TestEslResult | null>(null)

  async function testen() {
    setBusy(true)
    setResult(null)
    try {
      setResult(await sendTestEsl())
    } catch (err) {
      setResult({ ok: false, error: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <span className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-[0.95rem] font-medium text-text">Preisdienst testen</span>
          <span className="text-sm text-text-muted">
            Liest den Katalog mit dem hinterlegten Schlüssel — ändert keinen Preis
          </span>
        </span>
        <button
          type="button"
          className="btn-primary shrink-0"
          disabled={busy}
          onClick={() => void testen()}
        >
          {busy ? 'Testet …' : 'Testen'}
        </button>
      </span>
      {result && (
        <div aria-live="polite" className="space-y-1 text-sm">
          {result.ok ? (
            <>
              <p className="text-text">
                Verbunden — {result.count} Artikel in der Collection J4P
                {result.count !== 47 ? ' (laut Anleitung sollten es 47 sein)' : ''}.
              </p>
              {result.sample && result.sample.length > 0 && (
                <ul className="list-disc pl-5 text-text-muted">
                  {result.sample.map((zeile) => (
                    <li key={zeile}>{zeile}</li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              {result.hint && <p className="font-medium text-text">{result.hint}</p>}
              {result.error && (
                <p className="break-all font-mono text-xs text-text-muted">{result.error}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function TestCallRow() {
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<TestCallResult | null>(null)

  async function anrufen() {
    setBusy(true)
    setResult(null)
    try {
      setResult(await sendTestCall(to.trim() || undefined))
    } catch (err) {
      setResult({ ok: false, error: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <span className="text-[0.95rem] font-medium text-text">Testanruf</span>
      <span className="text-sm text-text-muted">
        Leer: an die erste Person der Rufbereitschaft (vorher speichern).
      </span>
      <span className="flex gap-2">
        <input
          type="tel"
          className="input min-w-0 flex-1"
          placeholder="+49 170 1234567"
          aria-label="Nummer für den Testanruf"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <button
          type="button"
          className="btn-primary shrink-0"
          disabled={busy}
          onClick={() => void anrufen()}
        >
          {busy ? 'Wählt …' : 'Anrufen'}
        </button>
      </span>
      {result && (
        <div aria-live="polite" className="space-y-1 text-sm">
          {result.ok ? (
            <p className="text-text">
              K4ia ruft {result.name ?? 'an'} an. Klingelt nichts, im Protokoll von ElevenLabs unter
              „Call History“ nachsehen.
            </p>
          ) : (
            <>
              {result.hint && <p className="font-medium text-text">{result.hint}</p>}
              {result.error && (
                <p className="break-all font-mono text-xs text-text-muted">{result.error}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** Eine Adresse zum Abschreiben oder Kopieren. */
function LinkRow({ label, hint, url }: { label: string; hint: string; url: string }) {
  return (
    <div className="flex flex-col gap-1 px-5 py-4">
      <span className="text-[0.95rem] font-medium text-text">{label}</span>
      <span className="text-xs text-text-muted">{hint}</span>
      <span className="flex items-center gap-2">
        <code className="min-w-0 flex-1 break-all font-mono text-sm">{url}</code>
        <button
          type="button"
          className="btn-chip shrink-0"
          onClick={() => void navigator.clipboard?.writeText(url)}
        >
          Kopieren
        </button>
      </span>
    </div>
  )
}

/** Echt oder Attrappe — auf einen Blick, nicht nur über die Farbe (Text steht dabei). */
function LiveBadge({ mock }: { mock: boolean }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider ${
        mock ? 'bg-surface-2 text-text-muted' : 'bg-surface-2 text-status-active'
      }`}
    >
      {mock ? 'Attrappe' : '● Live'}
    </span>
  )
}

function Unit({ unit, children }: { unit: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-2">
      <span className="flex-1">{children}</span>
      <span className="w-10 text-sm text-text-muted">{unit}</span>
    </span>
  )
}

/** Ein Schalter wie in iOS; darunter ein echtes Kontrollkästchen für Tastatur und Screenreader. */
function Switch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <span className="relative inline-flex">
      <input
        type="checkbox"
        role="switch"
        className="peer sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden="true"
        className="h-[31px] w-[51px] rounded-full bg-[rgba(120,120,128,0.36)] transition-colors peer-checked:bg-status-active peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-[2px] top-[2px] h-[27px] w-[27px] rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"
      />
    </span>
  )
}
